/* eslint react-hooks/set-state-in-effect: ["warn"] */
import { useEffect, useState } from "react";
import defaultPhotoUrl from "../../assets/svg/generic-profile.svg";
import { useQuery } from "@tanstack/react-query";
import { INTERVAL } from "../../constants/clientTime";
import { getGraphAvatar, getGraphDisplayName } from "./core.service";

function useAvatarImageFromGraph(userIdOrEmail: string) {
  const result = useQuery({
    queryKey: ["userAvatarFromGraph", userIdOrEmail],
    queryFn: async () => {
      try {
        const blob = await getGraphAvatar(userIdOrEmail);
        return blob ?? defaultPhotoUrl;
      } catch {
        return defaultPhotoUrl;
      }
    },
    enabled: !!userIdOrEmail,
    refetchOnWindowFocus: false,
    staleTime: 1 * INTERVAL.WEEK,
    gcTime: 1 * INTERVAL.WEEK, // ponytail: keep in sync with staleTime
  });
  return result?.data;
}

function useAvatarImagesFromGraph(userIdOrEmails: string[]) {
  const sortedIds = [...new Set(userIdOrEmails)].sort();

  const result = useQuery({
    queryKey: ["userAvatarsFromGraph", sortedIds],
    queryFn: async () => {
      const results = await Promise.all(
        sortedIds.map(async (userIdOrEmail) => {
          try {
            const avatar = await getGraphAvatar(userIdOrEmail);
            return { userIdOrEmail, avatar: avatar ?? defaultPhotoUrl };
          } catch {
            return { userIdOrEmail, avatar: defaultPhotoUrl };
          }
        })
      );
      return results.reduce<Record<string, Blob | string>>(
        (acc, { userIdOrEmail, avatar }) => {
          acc[userIdOrEmail] = avatar;
          return acc;
        },
        {}
      );
    },
    enabled: userIdOrEmails.length > 0,
    refetchOnWindowFocus: false,
    staleTime: 1 * INTERVAL.WEEK,
    gcTime: 1 * INTERVAL.WEEK, // ponytail: keep in sync with staleTime
  });
  return result?.data;
}

export function useAvatarImage(uid: string) {
  const [avatarUrl, setAvatarUrl] = useState<string>(defaultPhotoUrl);
  const avatarImage = useAvatarImageFromGraph(uid);

  useEffect(() => {
    if (avatarImage instanceof Blob) {
      const objectUrl = URL.createObjectURL(avatarImage);
      setAvatarUrl(objectUrl);
      return () => {
        URL.revokeObjectURL(objectUrl);
      };
    } else if (avatarImage) {
      setAvatarUrl(avatarImage);
    }
    return () => {};
  }, [avatarImage]);

  return avatarUrl;
}

export function useAvatarImages(uids: string[]) {
  const [avatarUrls, setAvatarUrls] = useState<Record<string, string>>({});
  const avatarImages = useAvatarImagesFromGraph(uids);

  useEffect(() => {
    if (avatarImages) {
      const newAvatarUrls: Record<string, string> = {};
      Object.entries(avatarImages).forEach(([uid, avatarImage]) => {
        if (avatarImage instanceof Blob) {
          const objectUrl = URL.createObjectURL(avatarImage);
          newAvatarUrls[uid] = objectUrl;
        } else {
          newAvatarUrls[uid] = avatarImage;
        }
      });
      setAvatarUrls(newAvatarUrls);
      return () => {
        Object.values(newAvatarUrls).forEach((url) => {
          // Only revoke URLs that were created from Blob (object URLs)
          if (url.startsWith("blob:")) {
            URL.revokeObjectURL(url);
          }
        });
      };
    }
    return () => {};
  }, [avatarImages]);

  return avatarUrls;
}

export function useGraphDisplayName(email: string): string | undefined {
  const { data } = useQuery<string | undefined>({
    queryKey: ["graphDisplayName", email],
    queryFn: async () => {
      const result = await getGraphDisplayName(email);
      return result.displayName ?? undefined;
    },
    enabled: !!email,
    staleTime: 1 * INTERVAL.WEEK,
    gcTime: 1 * INTERVAL.WEEK, // ponytail: keep in sync with staleTime
    refetchOnWindowFocus: false,
  });

  return data;
}
